import React, { useEffect, useState } from "react";
import { Card, Row, Col, Badge, Spinner, Alert } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { fetchLivreursPositions } from "./DashboardService";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Fix for default marker icons in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function LiveursLocations() {
  const [livreurs, setLivreurs] = useState([]);
  const [stats, setStats] = useState({ total_online: 0, available: 0, delivering: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour récupérer les positions des livreurs
  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchLivreursPositions();

      console.log("Response livreurs:", response); // DEBUG

      if (response.success) {
        const livreursData = response.livreurs || [];
        console.log("Livreurs data:", livreursData); // DEBUG

        // Vérifier si les livreurs ont des coordonnées
        livreursData.forEach(l => {
          console.log(`Livreur ${l.name}: lat=${l.latitude}, lng=${l.longitude}, last_lat=${l.last_latitude}, last_lng=${l.last_longitude}`); // DEBUG
        });

        setLivreurs(livreursData);
        setStats({
          total_online: response.total_online || 0,
          available: response.available || 0,
          delivering: response.delivering || 0
        });
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des positions:", err);
      setError("Impossible de charger les positions des livreurs");
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial et rafraîchissement automatique toutes les 30 secondes
  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fonction pour obtenir le style du badge selon le statut
  const getStatusBadge = (status, isOnline) => {
    if (!isOnline) {
      return <Badge bg="secondary">Hors ligne</Badge>;
    }

    switch (status) {
      case "available":
        return <Badge bg="success">Disponible</Badge>;
      case "delivering":
        return <Badge bg="primary">En livraison</Badge>;
      case "busy":
        return <Badge bg="warning">Occupé</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Fonction pour formater la durée depuis la dernière mise à jour
  const formatLastUpdate = (minutes) => {
    if (minutes === 0) {
      return "À l'instant";
    } else if (minutes === 1) {
      return "Il y a 1 minute";
    } else if (minutes < 60) {
      return `Il y a ${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      return hours === 1 ? "Il y a 1 heure" : `Il y a ${hours} heures`;
    }
  };

  // Fonction pour ouvrir Google Maps avec les coordonnées
  const openInGoogleMaps = (latitude, longitude, name) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  if (loading && livreurs.length === 0) {
    return (
      <Card className="border-0 mb-4">
        <Card.Body className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
          <p className="mt-2">Chargement des positions des livreurs...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      <Card className="border-0 mb-4">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center">
            <span>Livreurs en ligne</span>
            <div>
              <Badge bg="info" className="me-2">
                {stats.total_online} en ligne
              </Badge>
              <Badge bg="success" className="me-2">
                {stats.available} disponibles
              </Badge>
              <Badge bg="primary">
                {stats.delivering} en livraison
              </Badge>
            </div>
          </Card.Title>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {livreurs.length === 0 && !loading && (
            <Alert variant="info" className="mt-3">
              Aucun livreur en ligne pour le moment
            </Alert>
          )}

          {livreurs.length > 0 && (
            <Row className="mt-3">
              {livreurs.map((livreur) => (
                <Col key={livreur.livreur_id} lg={4} md={6} sm={12} className="mb-3">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="mb-1">{livreur.name}</h5>
                          <small className="text-muted">{livreur.phone}</small>
                        </div>
                        {getStatusBadge(livreur.status, livreur.is_online)}
                      </div>

                      {livreur.latitude && livreur.longitude ? (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">
                            Position GPS: {livreur.latitude}, {livreur.longitude}
                          </small>
                          <div style={{
                            height: "200px",
                            width: "100%",
                            marginBottom: "10px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "2px solid #dee2e6"
                          }}>
                            <MapContainer
                              center={[parseFloat(livreur.latitude), parseFloat(livreur.longitude)]}
                              zoom={14}
                              style={{ height: "100%", width: "100%" }}
                              scrollWheelZoom={false}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              <Marker position={[parseFloat(livreur.latitude), parseFloat(livreur.longitude)]}>
                                <Popup>
                                  <strong>{livreur.name}</strong><br />
                                  {livreur.phone}<br />
                                  Statut: {livreur.status}
                                </Popup>
                              </Marker>
                            </MapContainer>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary w-100"
                            onClick={() => openInGoogleMaps(
                              livreur.latitude,
                              livreur.longitude,
                              livreur.name
                            )}
                            title="Voir sur Google Maps"
                          >
                            📍 Ouvrir dans Google Maps
                          </button>
                        </div>
                      ) : livreur.last_latitude && livreur.last_longitude ? (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">
                            Position GPS: {livreur.last_latitude}, {livreur.last_longitude}
                          </small>
                          <div style={{
                            height: "200px",
                            width: "100%",
                            marginBottom: "10px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "2px solid #dee2e6"
                          }}>
                            <MapContainer
                              center={[parseFloat(livreur.last_latitude), parseFloat(livreur.last_longitude)]}
                              zoom={14}
                              style={{ height: "100%", width: "100%" }}
                              scrollWheelZoom={false}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              <Marker position={[parseFloat(livreur.last_latitude), parseFloat(livreur.last_longitude)]}>
                                <Popup>
                                  <strong>{livreur.name}</strong><br />
                                  {livreur.phone}<br />
                                  Statut: {livreur.status}
                                </Popup>
                              </Marker>
                            </MapContainer>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary w-100"
                            onClick={() => openInGoogleMaps(
                              livreur.last_latitude,
                              livreur.last_longitude,
                              livreur.name
                            )}
                            title="Voir sur Google Maps"
                          >
                            📍 Ouvrir dans Google Maps
                          </button>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <small className="text-muted">Aucune position GPS disponible</small>
                        </div>
                      )}

                      {livreur.last_update && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Dernière mise à jour:</small>
                          <small>
                            {formatLastUpdate(livreur.minutes_since_last_update || 0)}
                          </small>
                        </div>
                      )}

                      {livreur.shift_started_at && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Service démarré:</small>
                          <small>
                            {new Date(livreur.shift_started_at).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </div>
                      )}

                      {livreur.current_commande_id && (
                        <div className="mt-2">
                          <Badge bg="info">
                            Commande N°{livreur.current_commande_id}
                          </Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          <div className="mt-3 text-end">
            <small className="text-muted">
              Mise à jour automatique toutes les 30 secondes
            </small>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default LiveursLocations;
